import React from "react";
import { AlertIcon, CheckIcon, GlobeIcon } from "./sheetIcons";
import { ConnectBody, ConnectFoot, PrimaryButton, SecondaryButton } from "./connect/ConnectScreen";
import type { MasspayKycStatus } from "../types/payout";
import { masspayOnHold } from "../lib/connectBank";
import "./MasspayFaces.css";
import { t } from "../i18n";

// The MassPay steps of "Connect your bank" (components/ConnectBankSheet), for
// talent in a country Stripe cannot pay. Full screen since 1.5.2 (founder,
// 2026-10-19 — .claude/1.5.2-connect-bank-fullscreen.html); the wording is the
// founder's picks of 2026-10-09:
//
//   name     their name as on their ID — MassPay's ID check (Veriff) compares it
//   waiting  what's under the ID check (in-app browser, or the web's new tab)
//            when the talent comes back: "I'm done" / "Open it again"
//   review   the purple checkmark: "You're in review"
//   retry    the ID check asked for another try
//   hold     MassPay put the payee on hold: support sorts it out
//   elsewhere connecting from a country Stripe pays: stopped before the paid check
//
// The first MassPay screen ("{country} selected", the wallets) is
// connect/RailScreens MasspayRail. Every step has a way forward (CLAUDE.md
// "cover every path"): try again, change country, or support.
export type MasspayFace = "mpAsk" | "mpName" | "mpWaiting" | "mpReview" | "mpRetry" | "mpHold" | "mpElsewhere";

/** Which face a MassPay set-up state opens on ("mpAsk" = the start); "verified" = done, the review sheet takes over. */
export function masspayFace(m: { kycStatus?: MasspayKycStatus | string | null; status?: string | null } | null | undefined): MasspayFace | "verified" {
  if (!m) return "mpAsk";
  if (masspayOnHold(m.status)) return "mpHold";
  switch (m.kycStatus) {
    case "approved":
      return "verified";
    case "review":
      return "mpReview";
    case "declined":
    case "resubmission":
      return "mpRetry";
    default:
      return "mpAsk"; // none, expired: start (again)
  }
}

export const SUPPORT_EMAIL = "hello@requesta.app";

const SupportLink = ({ primary }: { primary?: boolean }) => (
  <a className={primary ? "cxs-primary" : "cxs-secondary"} href={`mailto:${SUPPORT_EMAIL}`}>{t("connectBankSheet.contactSupport")}</a>
);

// "Not in Indonesia? Change country".
const ChangeCountryLink = ({ country, onClick, disabled }: { country: string; onClick: () => void; disabled?: boolean }) => (
  <button type="button" className="cx-link" onClick={onClick} disabled={disabled}>{t("connectBankSheet.notInChangeCountry", { country })}</button>
);

export const Orb = ({ tone, children }: { tone?: "red" | "amber"; children: React.ReactNode }) => (
  <div className="cbs-orb-wrap" aria-hidden="true">
    <span className="cbs-spark cbs-spark-1">✦</span>
    <span className={`cbs-orb${tone ? ` mpf-orb-${tone}` : ""}`}>{children}</span>
    <span className="cbs-spark cbs-spark-2">✦</span>
  </div>
);

// A short step in the middle of the screen: orb, title, line.
export const Middle = ({ orb, title, sub, children }: { orb: React.ReactNode; title: string; sub: string; children?: React.ReactNode }) => (
  <ConnectBody center>
    {orb}
    <h2 id="cx-title" className="cx-title">{title}</h2>
    <p className="cx-sub">{sub}</p>
    {children}
  </ConnectBody>
);

const ErrorLine = ({ error }: { error?: string }) => (error ? <p className="cx-error" role="alert">{error}</p> : null);

export function MpName(props: {
  first: string;
  last: string;
  onFirst: (v: string) => void;
  onLast: (v: string) => void;
  pending: boolean;
  error: string;
  support: boolean;
  onSubmit: () => void;
}) {
  return (
    <>
      <ConnectBody>
        <form
          id="mpf-name"
          className="mpf-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (!props.pending) props.onSubmit();
          }}
        >
          <h2 id="cx-title" className="cx-title cx-title-left">{t("connectBankSheet.yourNameAsOn")}</h2>
          <p className="cx-sub cx-sub-left">{t("connectBankSheet.weCheckItAgainst")}</p>
          <label className="mpf-field">
            <span>{t("connectBankSheet.firstName")}</span>
            <input value={props.first} onChange={(e) => props.onFirst(e.target.value)} autoComplete="given-name" autoCapitalize="words" maxLength={50} disabled={props.pending} />
          </label>
          <label className="mpf-field">
            <span>{t("connectBankSheet.lastName")}</span>
            <input value={props.last} onChange={(e) => props.onLast(e.target.value)} autoComplete="family-name" autoCapitalize="words" maxLength={50} disabled={props.pending} />
          </label>
          <p className="mpf-hint">{t("connectBankSheet.onlyOneName")}</p>
          {props.error && <p className="cx-error mpf-error" role="alert">{props.error}</p>}
        </form>
      </ConnectBody>
      <ConnectFoot>
        <PrimaryButton type="submit" form="mpf-name" disabled={props.pending} busy={props.pending}>
          {props.pending ? t("connectBankSheet.openingIdCheck") : t("connectBankSheet.continueToIdCheck")}
        </PrimaryButton>
        {props.support && <SupportLink />}
        <p className="cx-fine">{t("connectBankSheet.idCheckByMasspay")}</p>
      </ConnectFoot>
    </>
  );
}

// An ID card, for "Finish your ID check".
const IdIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="5" width="18" height="14" rx="2.5" /><circle cx="9" cy="11" r="2.2" /><path d="M5.8 16.2c.7-1.6 1.8-2.4 3.2-2.4s2.5.8 3.2 2.4" /><path d="M14.5 10h4M14.5 13.5h3" />
  </svg>
);

export function MpWaiting(props: { pending: boolean; note: string; error: string; onDone: () => void; onReopen: () => void }) {
  return (
    <>
      <Middle orb={<Orb><IdIcon /></Orb>} title={t("connectBankSheet.finishYourIdCheck")} sub={t("connectBankSheet.comeBackHereWhen")}>
        {props.note && <p className="cx-note" role="status">{props.note}</p>}
      </Middle>
      <ConnectFoot>
        <ErrorLine error={props.error} />
        <PrimaryButton onClick={props.onDone} disabled={props.pending} busy={props.pending}>{t("connectBankSheet.iMDone")}</PrimaryButton>
        <SecondaryButton onClick={props.onReopen} disabled={props.pending}>{t("connectBankSheet.openItAgain")}</SecondaryButton>
      </ConnectFoot>
    </>
  );
}

export function MpReview({ onClose }: { onClose: () => void }) {
  return (
    <>
      <Middle orb={<Orb><CheckIcon /></Orb>} title={t("connectBankSheet.youReInReview")} sub={t("connectBankSheet.weLlNotifyYou")} />
      <ConnectFoot>
        <PrimaryButton onClick={onClose} noChevron>{t("connectBankSheet.close")}</PrimaryButton>
      </ConnectFoot>
    </>
  );
}

export function MpRetry(props: { country: string; canChangeCountry: boolean; onRetry: () => void; onChangeCountry: () => void }) {
  return (
    <>
      <Middle orb={<Orb tone="red"><AlertIcon /></Orb>} title={t("connectBankSheet.idCheckDidnT")} sub={t("connectBankSheet.tryAgainWithA")} />
      <ConnectFoot>
        <PrimaryButton onClick={props.onRetry}>{t("connectBankSheet.tryAgain")}</PrimaryButton>
        <SupportLink />
        {props.canChangeCountry && <ChangeCountryLink country={props.country} onClick={props.onChangeCountry} />}
      </ConnectFoot>
    </>
  );
}

export function MpHold({ onClose }: { onClose: () => void }) {
  return (
    <>
      <Middle orb={<Orb tone="amber"><AlertIcon /></Orb>} title={t("connectBankSheet.payoutsOnHold")} sub={t("connectBankSheet.contactUsAndWe")} />
      <ConnectFoot>
        <SupportLink primary />
        <SecondaryButton onClick={onClose}>{t("connectBankSheet.close")}</SecondaryButton>
      </ConnectFoot>
    </>
  );
}

// A map pin, for "Looks like you're in France".
const PinIcon = () => (
  <svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 21s-7-6.2-7-11.5A7 7 0 0 1 19 9.5C19 14.8 12 21 12 21z" />
    <circle cx="12" cy="9.5" r="2.5" />
  </svg>
);

/**
 * The connection is in a country Stripe pays (server location_mismatch,
 * 2026-10-16): stopped BEFORE MassPay's paid ID check. Living there → change
 * country (Stripe). Really at home but on a VPN or a foreign SIM's roaming
 * data (which can look like the SIM's country) → VPN off / local Wi-Fi.
 */
export function MpElsewhere(props: { detected: string; home: string; canChangeCountry: boolean; onChangeCountry: () => void; onClose: () => void }) {
  return (
    <>
      <Middle
        orb={<Orb tone="amber"><PinIcon /></Orb>}
        title={t("connectBankSheet.looksLikeYouReIn", { country: props.detected })}
        sub={t("connectBankSheet.liveThereChangeYour", { country: props.detected, home: props.home })}
      />
      <ConnectFoot>
        {props.canChangeCountry && <PrimaryButton onClick={props.onChangeCountry}>{t("connectBankSheet.changeCountry")}</PrimaryButton>}
        <SecondaryButton onClick={props.onClose}>{t("connectBankSheet.close")}</SecondaryButton>
      </ConnectFoot>
    </>
  );
}

/** "Change your country?" — both rails; allowed while nothing is verified or earned (server lib/payoutCountry.js). */
export function ChangeCountryFace(props: { pending: boolean; error: string; support: boolean; onConfirm: () => void; onBack: () => void }) {
  return (
    <>
      <Middle orb={<Orb><GlobeIcon /></Orb>} title={t("connectBankSheet.changeYourCountry")} sub={t("connectBankSheet.youLlPickIt")} />
      <ConnectFoot>
        <ErrorLine error={props.error} />
        {props.support ? <SupportLink primary /> : (
          <PrimaryButton onClick={props.onConfirm} disabled={props.pending} busy={props.pending}>{t("connectBankSheet.changeCountry")}</PrimaryButton>
        )}
        <SecondaryButton onClick={props.onBack} disabled={props.pending}>{t("connectBankSheet.back")}</SecondaryButton>
      </ConnectFoot>
    </>
  );
}
